import { useMemo } from 'react';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Target, Activity, Minus } from 'lucide-react';
import { getSportAccent } from '../../config/sportAccents';
import type { SportRecommendation, IntelligenceSignal } from '../../types';

const SPORTS = ['mlb', 'wnba', 'nba', 'nfl'] as const;
type Sport = typeof SPORTS[number];

interface SportPressureData {
  sport: Sport;
  sportPicks: SportRecommendation[];
  sportSignals: IntelligenceSignal[];
  highCount: number;
  medCount: number;
  lowCount: number;
  betNowCount: number;
  pressureScore: number;
  dominantSignalType: string;
  marketCondition: 'Volatile' | 'Active' | 'Stable';
  hasData: boolean;
}

function computePressure(
  picks: SportRecommendation[],
  signals: IntelligenceSignal[],
  sport: Sport,
): SportPressureData {
  const sportPicks = picks.filter(
    (p) => p.leagueId === sport && p.decisionStatus !== 'NO_BET' && p.decisionStatus !== 'INVALID',
  );
  const sportSignals = signals.filter((s) => s.leagueId === sport && s.isActive);

  const highCount = sportPicks.filter((p) => p.confidence >= 0.70).length;
  const medCount  = sportPicks.filter((p) => p.confidence >= 0.55 && p.confidence < 0.70).length;
  const lowCount  = sportPicks.filter((p) => p.confidence < 0.55).length;
  const betNowCount = sportPicks.filter((p) => p.timing === 'BET_NOW').length;

  const avgEdge = sportPicks.length > 0
    ? sportPicks.reduce((sum, p) => sum + p.edge, 0) / sportPicks.length
    : 0;
  const edgeConcentration = Math.min(avgEdge / 8, 1);
  const signalScore = Math.min(sportSignals.length / 5, 1);
  const betNowWeight = betNowCount / Math.max(sportPicks.length, 1);
  const pressureScore = (edgeConcentration * 0.50) + (signalScore * 0.30) + (betNowWeight * 0.20);

  const signalCounts: Record<string, number> = {};
  for (const s of sportSignals) {
    if (s.type === 'steam_move' || s.type === 'sharp_action' || s.type === 'injury_downgrade') {
      signalCounts[s.type] = (signalCounts[s.type] ?? 0) + 1;
    }
  }
  const dominantSignalType =
    Object.entries(signalCounts).sort(([, a], [, b]) => b - a)[0]?.[0] ?? 'stable';

  const marketCondition: SportPressureData['marketCondition'] =
    pressureScore >= 0.65 ? 'Volatile' : pressureScore >= 0.35 ? 'Active' : 'Stable';
  const hasData = sportPicks.length > 0 || sportSignals.length > 0;

  return {
    sport, sportPicks, sportSignals,
    highCount, medCount, lowCount, betNowCount,
    pressureScore, dominantSignalType, marketCondition, hasData,
  };
}

// ── Signal pill metadata ──────────────────────────────────────────────────────

const SIGNAL_PILLS: Record<string, { icon: React.FC<{ className?: string }>; label: string; color: string }> = {
  steam_move:       { icon: TrendingUp, label: 'Steam',  color: 'text-warn' },
  sharp_action:     { icon: Target,     label: 'Sharp',  color: 'text-gold' },
  injury_downgrade: { icon: Activity,   label: 'Injury', color: 'text-loss' },
  stable:           { icon: Minus,      label: 'Stable', color: 'text-gray-600' },
};

const CONDITION_CFG: Record<string, { cls: string }> = {
  Volatile: { cls: 'text-gold bg-gold/10 border-gold/25' },
  Active:   { cls: 'text-warn bg-warn/10 border-warn/25' },
  Stable:   { cls: 'text-gray-500 bg-surface-3 border-surface-4' },
};

function heatBarGradient(score: number): string {
  if (score >= 0.66) return 'linear-gradient(to right, #f59e0b, #d97706)';
  if (score >= 0.33) return 'linear-gradient(to right, rgba(245,158,11,0.5), #f59e0b)';
  return 'linear-gradient(to right, rgba(8,145,178,0.5), #06b6d4)';
}

// ── Sport card ────────────────────────────────────────────────────────────────

function SportCard({ data, index }: { data: SportPressureData; index: number }) {
  const accent = getSportAccent(data.sport);
  const conditionCfg = CONDITION_CFG[data.marketCondition];
  const signalPill = SIGNAL_PILLS[data.dominantSignalType] ?? SIGNAL_PILLS.stable;
  const SignalIcon = signalPill.icon;
  const totalPicks = data.sportPicks.length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: data.hasData ? 1 : 0.4, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.2 }}
      className={clsx(
        'rounded-lg border p-2.5 space-y-2',
        data.hasData ? `${accent.bg} ${accent.border}` : 'bg-surface-2 border-surface-4',
      )}
    >
      {/* Sport header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-sm leading-none">{accent.emoji}</span>
          <span className={clsx(
            'text-[10px] font-bold uppercase tracking-wider',
            data.hasData ? accent.primary : 'text-gray-600',
          )}>
            {accent.label}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={clsx('px-1.5 py-0.5 rounded border text-[9px] font-bold', conditionCfg.cls)}>
            {data.marketCondition}
          </span>
          {data.betNowCount > 0 && (
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-gold/15 border border-gold/30 text-[9px] font-bold text-gold-bright">
              <span className="w-1 h-1 rounded-full bg-gold animate-pulse-slow" />
              {data.betNowCount}
            </span>
          )}
        </div>
      </div>

      {/* Heat bar */}
      <div className="space-y-0.5">
        <div className="h-1.5 bg-surface-3 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ background: heatBarGradient(data.pressureScore) }}
            initial={{ width: 0 }}
            animate={{ width: `${Math.max(data.pressureScore * 100, data.hasData ? 2 : 0)}%` }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: index * 0.08 }}
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[9px] text-gray-700 tabular-nums">
            {totalPicks} pick{totalPicks !== 1 ? 's' : ''}
          </span>
          <span className="text-[9px] text-gray-700 font-mono tabular-nums">
            {Math.round(data.pressureScore * 100)}
          </span>
        </div>
      </div>

      {/* Confidence tiers + dominant signal */}
      {totalPicks > 0 ? (
        <div className="flex items-center gap-2">
          {data.highCount > 0 && (
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-win" />
              <span className="text-[9px] text-gray-500 tabular-nums">{data.highCount}</span>
            </div>
          )}
          {data.medCount > 0 && (
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-warn" />
              <span className="text-[9px] text-gray-500 tabular-nums">{data.medCount}</span>
            </div>
          )}
          {data.lowCount > 0 && (
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-gray-600" />
              <span className="text-[9px] text-gray-500 tabular-nums">{data.lowCount}</span>
            </div>
          )}
          <div className="flex items-center gap-1 ml-auto">
            <SignalIcon className={clsx('w-2.5 h-2.5', signalPill.color)} />
            <span className={clsx('text-[9px]', signalPill.color)}>{signalPill.label}</span>
          </div>
        </div>
      ) : (
        <p className="text-[9px] text-gray-700 text-center">
          {data.sportSignals.length > 0 ? `${data.sportSignals.length} market signal${data.sportSignals.length !== 1 ? 's' : ''}` : 'monitoring futures'}
        </p>
      )}
    </motion.div>
  );
}

// ── Panel summary strip ───────────────────────────────────────────────────────

function PressureSummary({ data }: { data: SportPressureData[] }) {
  const avgPressure = data.reduce((sum, d) => sum + d.pressureScore, 0) / data.length;
  const hotSports = data.filter((d) => d.pressureScore >= 0.65);
  const totalBetNow = data.reduce((sum, d) => sum + d.betNowCount, 0);

  return (
    <div className="px-3 py-2 border-b border-surface-4 flex-shrink-0 flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="h-1 bg-surface-3 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ background: heatBarGradient(avgPressure) }}
            initial={{ width: 0 }}
            animate={{ width: `${avgPressure * 100}%` }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          />
        </div>
        <p className="text-[9px] text-gray-700 mt-0.5 uppercase tracking-widest truncate">
          {hotSports.length > 0
            ? `${hotSports.map((d) => getSportAccent(d.sport).label).join(' · ')} hot`
            : 'Market quiet'}
        </p>
      </div>
      {totalBetNow > 0 && (
        <span className="flex items-center gap-1 px-2 py-1 rounded-md bg-gold/10 border border-gold/25 text-[10px] font-bold text-gold-bright flex-shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
          {totalBetNow}
        </span>
      )}
    </div>
  );
}

// ── MarketPressureMap ─────────────────────────────────────────────────────────

interface MarketPressureMapProps {
  picks: SportRecommendation[];
  signals: IntelligenceSignal[];
  className?: string;
}

export function MarketPressureMap({ picks, signals, className }: MarketPressureMapProps) {
  const pressureData = useMemo(
    () => SPORTS.map((sport) => computePressure(picks, signals, sport)),
    [picks, signals],
  );

  return (
    <div className={clsx('flex flex-col h-full', className)}>
      <PressureSummary data={pressureData} />
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        <AnimatePresence initial={false}>
          {pressureData.map((data, i) => (
            <SportCard key={data.sport} data={data} index={i} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
